import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';

const api = axios.create({ baseURL: API_URL });

// Convert a data-URL to a Blob URL that browsers can display in a new tab
const openDataUrlInNewTab = (dataUrl) => {
  try {
    const [header, b64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: mime });
    window.open(URL.createObjectURL(blob), '_blank');
  } catch {
    window.open(dataUrl, '_blank');
  }
};

const FeedbackAdminScreen = () => {
  const { isAdmin, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Ref map for scrolling to a specific feedback row
  const rowRefs = useRef({});

  // Filter state
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 30;

  // Data state
  const [feedback, setFeedback] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  // Tracks a feedback_id to scroll into view once the list finishes loading
  const pendingScrollRef = useRef(null);

  // Edit state for admin notes / status / category
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [saving, setSaving] = useState(false);

  // Drag-and-drop state
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Set up auth header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Fetch feedback list
  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (category) params.category = category;
      if (status) params.status = status;
      if (search) params.search = search;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/feedback', { params });
      setFeedback(response.data.feedback || []);
      setPagination(response.data.pagination || { total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [page, category, status, search, startDate, endDate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/feedback/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch feedback stats:', err);
    }
  }, []);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Auto-select a feedback item when navigated from a notification
  const pendingFeedbackId = searchParams.get('feedbackId');
  useEffect(() => {
    if (!pendingFeedbackId || !token || loading) return;

    // Clear the query param immediately to prevent re-triggering
    const params = new URLSearchParams(searchParams);
    params.delete('feedbackId');
    setSearchParams(params, { replace: true });

    // Check if already in the loaded list
    const match = feedback.find(f => String(f.feedback_id) === pendingFeedbackId);
    if (match) {
      // Always select (never toggle-off) when arriving from a notification
      setSelectedFeedback(match);
      setEditNotes('');
      setEditStatus(match.status);
      setEditCategory(match.category);
      // Scroll after a brief tick so the selected style has rendered
      setTimeout(() => {
        rowRefs.current[match.feedback_id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      // Item may be filtered out or on a different page — fetch directly and select
      const fetchAndSelect = async () => {
        try {
          const response = await api.get(`/feedback/${pendingFeedbackId}`);
          if (response.data) {
            setSelectedFeedback(response.data);
            setEditNotes('');
            setEditStatus(response.data.status);
            setEditCategory(response.data.category);
            // Clear all filters and jump to the page containing this item.
            // pendingScrollRef triggers the scroll once loading completes.
            pendingScrollRef.current = response.data.feedback_id;
            setCategory('');
            setStatus('');
            setSearch('');
            setStartDate('');
            setEndDate('');
            setPage(response.data.page_in_list || 1);
          }
        } catch {
          // Feedback item not found — ignore
        }
      };
      fetchAndSelect();
    }
  }, [pendingFeedbackId, loading, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to a pending item once the list finishes loading (used after page jump)
  useEffect(() => {
    if (loading || !pendingScrollRef.current) return;
    const id = pendingScrollRef.current;
    pendingScrollRef.current = null;
    setTimeout(() => {
      rowRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [loading]);

  // ── Polling: refresh feedback data periodically ────────────────────────────
  useEffect(() => {
    if (!token) return;

    const intervalId = setInterval(() => {
      fetchFeedback();
      fetchStats();
    }, 30_000);

    return () => clearInterval(intervalId);
  }, [token, fetchFeedback, fetchStats]);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setCategory('');
    setStatus('active');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleSelectFeedback = (item) => {
    if (selectedFeedback?.feedback_id === item.feedback_id) {
      setSelectedFeedback(null);
    } else {
      setSelectedFeedback(item);
      setEditNotes('');
      setEditStatus(item.status);
      setEditCategory(item.category);
    }
  };

  const handleSave = async () => {
    if (!selectedFeedback) return;
    setSaving(true);
    try {
      const response = await api.patch(`/feedback/${selectedFeedback.feedback_id}`, {
        status: editStatus,
        admin_notes: editNotes,
        category: editCategory,
      });
      // Update in list
      setFeedback(prev => prev.map(f =>
        f.feedback_id === selectedFeedback.feedback_id ? { ...f, ...response.data } : f
      ));
      setSelectedFeedback(prev => ({ ...prev, ...response.data }));
      setEditNotes(''); // Clear notes input after save
      fetchStats(); // Refresh counters
      fetchFeedback(); // Re-fetch list to apply active filters
    } catch (err) {
      console.error('Failed to update feedback:', err);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback? This cannot be undone.')) return;
    try {
      await api.delete(`/feedback/${id}`);
      setFeedback(prev => prev.filter(f => f.feedback_id !== id));
      if (selectedFeedback?.feedback_id === id) setSelectedFeedback(null);
      fetchStats();
    } catch (err) {
      console.error('Failed to delete feedback:', err);
      alert('Failed to delete feedback');
    }
  };

  // ── Drag-and-drop handlers ──────────────────────────────────────────────

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image semi-transparent
    if (e.currentTarget) {
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const fromIndex = dragItem.current;
    const toIndex = dragOverItem.current;

    if (fromIndex === null || toIndex === null || fromIndex === toIndex) {
      handleDragEnd();
      return;
    }

    // Reorder the list locally
    const reordered = [...feedback];
    const [movedItem] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, movedItem);

    // Recalculate priorities: first item = 1, second = 2, etc.
    const withPriority = reordered.map((item, idx) => ({
      ...item,
      priority: idx + 1
    }));

    setFeedback(withPriority);
    handleDragEnd();

    // Persist to backend
    try {
      await api.patch('/feedback/reorder', {
        items: withPriority.map(item => ({
          feedback_id: item.feedback_id,
          priority: item.priority
        }))
      });
    } catch (err) {
      console.error('Failed to save priority order:', err);
      // Revert on failure
      fetchFeedback();
    }
  };

  // ── Formatting helpers ────────────────────────────────────────────────────

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric',
      minute: '2-digit', hour12: true
    });
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
    });
  };

  const formatStatus = (status) => {
    const labels = {
      new: 'New', reviewed: 'Reviewed', in_progress: 'In Progress',
      resolved: 'Resolved', declined: 'Declined'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#3182ce', reviewed: '#d69e2e', in_progress: '#dd6b20',
      resolved: '#38a169', declined: '#e53e3e'
    };
    return colors[status] || '#718096';
  };

  const categoryStyle = (cat) => {
    switch (cat) {
      case 'bug': return { bg: '#fff5f5', text: '#c53030', border: '#fed7d7', label: '🐛 Bug' };
      case 'feature_request': return { bg: '#faf5ff', text: '#6b46c1', border: '#e9d8fd', label: '✨ Feature' };
      case 'ui_ux': return { bg: '#fffaf0', text: '#c05621', border: '#feebc8', label: '🎨 UI/UX' };
      case 'general': return { bg: '#ebf8ff', text: '#2b6cb0', border: '#bee3f8', label: '💬 General' };
      default: return { bg: '#f7fafc', text: '#4a5568', border: '#e2e8f0', label: cat };
    }
  };

  const statusStyle = (s) => {
    switch (s) {
      case 'new': return { bg: '#ebf8ff', text: '#2b6cb0', border: '#bee3f8' };
      case 'reviewed': return { bg: '#fefce8', text: '#a16207', border: '#fef08a' };
      case 'in_progress': return { bg: '#faf5ff', text: '#7c3aed', border: '#e9d8fd' };
      case 'resolved': return { bg: '#f0fff4', text: '#276749', border: '#c6f6d5' };
      case 'declined': return { bg: '#f7fafc', text: '#718096', border: '#e2e8f0' };
      default: return { bg: '#f7fafc', text: '#4a5568', border: '#e2e8f0' };
    }
  };

  const statusLabel = (s) => {
    switch (s) {
      case 'new': return 'New';
      case 'reviewed': return 'Reviewed';
      case 'in_progress': return 'In Progress';
      case 'resolved': return 'Resolved';
      case 'declined': return 'Declined';
      default: return s;
    }
  };

  if (!isAdmin) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Admin access required</div>;
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#333', margin: '0 0 4px 0', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Feedback Admin
        </h1>
        <p style={{ color: '#888', fontSize: '0.82rem', margin: 0 }}>
          Review and manage user feedback submissions
        </p>
      </div>

      {/* Stats Dashboard */}
      {stats?.statuses && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <StatCard
            label="New"
            value={stats.statuses.new_count}
            color="#2b6cb0"
            active={status === 'new'}
            onClick={() => { setStatus(status === 'new' ? '' : 'new'); setPage(1); }}
          />
          <StatCard
            label="Reviewed"
            value={stats.statuses.reviewed_count}
            color="#a16207"
            active={status === 'reviewed'}
            onClick={() => { setStatus(status === 'reviewed' ? '' : 'reviewed'); setPage(1); }}
          />
          <StatCard
            label="In Progress"
            value={stats.statuses.in_progress_count}
            color="#7c3aed"
            active={status === 'in_progress'}
            onClick={() => { setStatus(status === 'in_progress' ? '' : 'in_progress'); setPage(1); }}
          />
          <StatCard
            label="Resolved"
            value={stats.statuses.resolved_count}
            color="#276749"
            active={status === 'resolved'}
            onClick={() => { setStatus(status === 'resolved' ? '' : 'resolved'); setPage(1); }}
          />
          <StatCard
            label="Declined"
            value={stats.statuses.declined_count}
            color="#718096"
            active={status === 'declined'}
            onClick={() => { setStatus(status === 'declined' ? '' : 'declined'); setPage(1); }}
          />
          <StatCard
            label="Total"
            value={stats.statuses.total}
            color="#333"
          />
        </div>
      )}

      {/* Trend */}
      {stats?.trend && (
        <div style={{
          display: 'flex', gap: '20px', marginBottom: '20px', padding: '12px 16px',
          backgroundColor: '#f0f4ff', borderRadius: '8px', border: '1px solid #dbeafe',
          fontSize: '13px', color: '#1e40af', flexWrap: 'wrap'
        }}>
          <span>📊 <strong>Last 24h:</strong> {stats.trend.last_24h}</span>
          <span>📊 <strong>Last 7d:</strong> {stats.trend.last_7d}</span>
          <span>📊 <strong>Last 30d:</strong> {stats.trend.last_30d}</span>
          {stats.categories?.length > 0 && (
            <span style={{ marginLeft: 'auto', color: '#4a5568' }}>
              Top: {stats.categories.slice(0, 3).map(c => {
                const cs = categoryStyle(c.category);
                return `${cs.label} (${c.count})`;
              }).join(', ')}
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center'
      }}>
        <select
          value={category}
          onChange={handleFilterChange(setCategory)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd',
            fontSize: '13px', backgroundColor: '#fff', minWidth: '140px'
          }}
        >
          <option value="">All Categories</option>
          <option value="bug">🐛 Bug Report</option>
          <option value="feature_request">✨ Feature Request</option>
          <option value="ui_ux">🎨 UI/UX Issue</option>
          <option value="general">💬 General</option>
        </select>

        <select
          value={status}
          onChange={handleFilterChange(setStatus)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd',
            fontSize: '13px', backgroundColor: '#fff', minWidth: '130px'
          }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active Only</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="declined">Declined</option>
        </select>

        <input
          type="date"
          value={startDate}
          onChange={handleFilterChange(setStartDate)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px'
          }}
          placeholder="Start date"
        />
        <input
          type="date"
          value={endDate}
          onChange={handleFilterChange(setEndDate)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px'
          }}
          placeholder="End date"
        />

        <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchFeedback(); }} style={{ display: 'flex', gap: '6px' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search feedback..."
            style={{
              padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd',
              fontSize: '13px', minWidth: '160px'
            }}
          />
          <button type="submit" style={{
            padding: '8px 14px', borderRadius: '6px', border: '1px solid #ddd',
            backgroundColor: '#667eea', color: '#fff', fontSize: '13px',
            cursor: 'pointer', fontWeight: '500'
          }}>
            🔍
          </button>
        </form>

        {(category || status || search || startDate || endDate) && (
          <button onClick={handleClearFilters} style={{
            padding: '8px 14px', borderRadius: '6px', border: '1px solid #ddd',
            backgroundColor: '#f7f7f7', cursor: 'pointer', fontSize: '13px',
            color: '#666'
          }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* Results info */}
      <div style={{ marginBottom: '12px', fontSize: '12px', color: '#888' }}>
        {loading ? 'Loading...' : `${pagination.total} feedback item${pagination.total !== 1 ? 's' : ''}`}
        {pagination.totalPages > 1 && ` — Page ${page} of ${pagination.totalPages}`}
      </div>

      {/* Feedback List */}
      {!loading && feedback.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: '#999',
          backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #eee'
        }}>
          <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>📝</span>
          No feedback found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {feedback.map((item, index) => {
            const isSelected = selectedFeedback?.feedback_id === item.feedback_id;
            const cs = categoryStyle(item.category);
            const ss = statusStyle(item.status);
            const isDragging = dragIndex === index;
            const isDragOver = dragOverIndex === index && dragIndex !== index;

            return (
              <div key={item.feedback_id} ref={el => { rowRefs.current[item.feedback_id] = el; }}>
                {/* Row */}
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  onClick={() => handleSelectFeedback(item)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 40px 36px 90px 1fr 80px 100px 60px',
                    gap: '12px',
                    alignItems: 'center',
                    padding: '10px 14px',
                    backgroundColor: isDragging ? '#e8eeff' : isSelected ? '#f0f4ff' : '#fff',
                    borderRadius: '6px',
                    border: isDragOver
                      ? '2px dashed #667eea'
                      : isSelected
                        ? '1px solid #bee3f8'
                        : '1px solid #eee',
                    cursor: 'pointer',
                    transition: isDragging ? 'none' : 'all 0.15s ease',
                    fontSize: '13px',
                    opacity: isDragging ? 0.6 : 1,
                    userSelect: 'none',
                  }}
                  onMouseEnter={(e) => { if (!isSelected && !isDragging) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                  onMouseLeave={(e) => { if (!isSelected && !isDragging) e.currentTarget.style.backgroundColor = '#fff'; }}
                >
                  {/* Drag handle */}
                  <span
                    style={{
                      cursor: 'grab',
                      color: '#bbb',
                      fontSize: '16px',
                      textAlign: 'center',
                      lineHeight: '1',
                      userSelect: 'none',
                    }}
                    title="Drag to reorder priority"
                  >
                    ⠿
                  </span>

                  {/* ID */}
                  <span style={{
                    color: '#a0aec0',
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    textAlign: 'center',
                  }}
                    title={`Feedback ID: ${item.feedback_id}`}
                  >
                    #{item.feedback_id}
                  </span>

                  {/* Priority number */}
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    backgroundColor: item.priority > 0 ? '#667eea' : '#e2e8f0',
                    color: item.priority > 0 ? '#fff' : '#a0aec0',
                    fontSize: '11px',
                    fontWeight: '700',
                    flexShrink: 0,
                  }}>
                    {item.priority > 0 ? item.priority : '—'}
                  </span>
                  {/* Category badge */}
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    backgroundColor: cs.bg,
                    color: cs.text,
                    border: `1px solid ${cs.border}`,
                    fontSize: '11px',
                    fontWeight: '600',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}>
                    {cs.label}
                  </span>

                  {/* Description preview */}
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#333',
                    }}>
                      {item.description}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#999',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {item.username || 'Anonymous'} • {item.page_url || '—'}
                    </div>
                  </div>

                  {/* Status */}
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    backgroundColor: ss.bg,
                    color: ss.text,
                    border: `1px solid ${ss.border}`,
                    fontSize: '11px',
                    fontWeight: '600',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}>
                    {statusLabel(item.status)}
                  </span>

                  {/* Date */}
                  <span style={{ color: '#999', fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {formatDate(item.created_at)}
                  </span>

                  {/* Delete */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.feedback_id); }}
                    title="Delete feedback"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: '14px', color: '#ccc', padding: '4px',
                      borderRadius: '4px', transition: 'color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#e53e3e'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#ccc'; }}
                  >
                    🗑️
                  </button>
                </div>

                {/* Expanded Detail */}
                {isSelected && (
                  <div style={{
                    padding: '16px 20px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '0 0 8px 8px',
                    border: '1px solid #bee3f8',
                    borderTop: 'none',
                    marginBottom: '4px',
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                      {/* Full description */}
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Full Description
                        </label>
                        <div style={{
                          marginTop: '4px', padding: '12px', backgroundColor: '#fff', borderRadius: '6px',
                          border: '1px solid #e2e8f0', fontSize: '13px', color: '#333',
                          lineHeight: '1.6', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto'
                        }}>
                          {item.description}
                        </div>
                      </div>

                      {/* Context info */}
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Context
                        </label>
                        <div style={{
                          marginTop: '4px', padding: '12px', backgroundColor: '#fff', borderRadius: '6px',
                          border: '1px solid #e2e8f0', fontSize: '12px', color: '#555',
                        }}>
                          <div style={{ marginBottom: '4px' }}><strong>ID:</strong> #{item.feedback_id}</div>
                          <div style={{ marginBottom: '4px' }}><strong>User:</strong> {item.username || 'Anonymous'}{item.user_id ? ` (ID: ${item.user_id})` : ''}</div>
                          <div style={{ marginBottom: '4px' }}><strong>Page:</strong> {item.page_url || '—'}</div>
                          <div style={{ marginBottom: '4px' }}><strong>Submitted:</strong> {formatDateFull(item.created_at)}</div>
                          <div style={{ marginBottom: '4px' }}><strong>Screen:</strong> {item.screen_width ? `${item.screen_width}px` : '—'}</div>
                          <div style={{ marginBottom: '4px', wordBreak: 'break-all' }}>
                            <strong>Browser:</strong> {item.browser_info
                              ? item.browser_info.substring(0, 120) + (item.browser_info.length > 120 ? '...' : '')
                              : '—'}
                          </div>
                          {item.resolved_at && (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                              <div><strong>Resolved:</strong> {formatDateFull(item.resolved_at)}</div>
                              <div><strong>By:</strong> {item.resolved_by_username || '—'}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Screenshot */}
                    {item.metadata?.screenshot && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          📸 Page Screenshot
                        </label>
                        <div style={{
                          marginTop: '4px',
                          padding: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0',
                          textAlign: 'center',
                        }}>
                          <img
                            src={item.metadata.screenshot}
                            alt="Page screenshot at time of feedback"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '400px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              border: '1px solid #e2e8f0',
                            }}
                            onClick={() => openDataUrlInNewTab(item.metadata.screenshot)}
                            title="Click to view full size"
                          />
                        </div>
                      </div>
                    )}

                    {/* Admin Actions */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '160px 160px 1fr auto', gap: '12px', alignItems: 'end',
                    }}>
                      {/* Status dropdown */}
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '4px' }}>
                          Status
                        </label>
                        <select
                          value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value)}
                          style={{
                            width: '100%', padding: '8px 12px', borderRadius: '6px',
                            border: '1px solid #ddd', fontSize: '13px', backgroundColor: '#fff'
                          }}
                        >
                          <option value="new">New</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="declined">Declined</option>
                        </select>
                      </div>

                      {/* Category dropdown */}
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '4px' }}>
                          Category
                        </label>
                        <select
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          style={{
                            width: '100%', padding: '8px 12px', borderRadius: '6px',
                            border: '1px solid #ddd', fontSize: '13px', backgroundColor: '#fff'
                          }}
                        >
                          <option value="bug">🐛 Bug</option>
                          <option value="feature_request">✨ Feature Request</option>
                          <option value="ui_ux">🎨 UI/UX</option>
                          <option value="general">💬 General</option>
                        </select>
                      </div>

                      {/* Admin Notes */}
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '4px' }}>
                          Notes (for this change)
                        </label>
                        <textarea
                          value={editNotes}
                          onChange={(e) => setEditNotes(e.target.value)}
                          placeholder="Add notes for this change..."
                          rows={3}
                          style={{
                            width: '100%', padding: '8px 12px', borderRadius: '6px',
                            border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box',
                            resize: 'vertical', minHeight: '60px', fontFamily: 'inherit'
                          }}
                        />
                      </div>

                      {/* Save button */}
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                          padding: '8px 20px', borderRadius: '6px', border: 'none',
                          backgroundColor: saving ? '#a0aec0' : '#667eea', color: '#fff',
                          fontSize: '13px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {saving ? 'Saving...' : '💾 Save'}
                      </button>
                    </div>

                    {/* Change History Timeline */}
                    <div style={{ marginTop: '16px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                        Change History
                      </label>
                      <div style={{
                        backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0',
                        maxHeight: '250px', overflowY: 'auto',
                      }}>
                        {(selectedFeedback?.change_history || []).length === 0 ? (
                          <div style={{ padding: '12px 14px', fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>
                            No changes recorded yet
                          </div>
                        ) : (
                          [...selectedFeedback.change_history].reverse().map((entry, idx) => (
                            <div key={idx} style={{
                              padding: '10px 14px',
                              borderBottom: idx < selectedFeedback.change_history.length - 1 ? '1px solid #f0f0f0' : 'none',
                              fontSize: '12px',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: entry.notes ? '4px' : 0 }}>
                                <div>
                                  <span style={{ fontWeight: '600', color: '#333' }}>{entry.username}</span>
                                  {entry.from_status !== entry.to_status ? (
                                    <span style={{ color: '#666' }}>
                                      {' '}changed status{' '}
                                      <span style={{ textDecoration: 'line-through', color: '#999' }}>{formatStatus(entry.from_status)}</span>
                                      {' → '}
                                      <span style={{ fontWeight: '600', color: getStatusColor(entry.to_status) }}>{formatStatus(entry.to_status)}</span>
                                    </span>
                                  ) : (
                                    <span style={{ color: '#666' }}> updated</span>
                                  )}
                                </div>
                                <span style={{ color: '#aaa', fontSize: '11px', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                  {formatDateFull(entry.timestamp)}
                                </span>
                              </div>
                              {entry.notes && (
                                <div style={{ color: '#555', fontStyle: 'italic', marginTop: '2px', paddingLeft: '2px' }}>
                                  "{entry.notes}"
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: '8px', marginTop: '20px',
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd',
              backgroundColor: page <= 1 ? '#f5f5f5' : '#fff', cursor: page <= 1 ? 'default' : 'pointer',
              fontSize: '13px', color: page <= 1 ? '#ccc' : '#333',
            }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: '13px', color: '#666' }}>
            Page {page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
            style={{
              padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd',
              backgroundColor: page >= pagination.totalPages ? '#f5f5f5' : '#fff',
              cursor: page >= pagination.totalPages ? 'default' : 'pointer',
              fontSize: '13px', color: page >= pagination.totalPages ? '#ccc' : '#333',
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

// ── StatCard ────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, color, active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      backgroundColor: active ? `${color}11` : '#fff',
      border: active ? `2px solid ${color}` : '1px solid #eee',
      borderRadius: '10px',
      padding: '14px 16px',
      textAlign: 'center',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s ease',
    }}
    onMouseEnter={(e) => { if (onClick) e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={(e) => { if (onClick) e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    <div style={{
      fontSize: '1.5rem', fontWeight: '700', color,
      lineHeight: 1.1,
    }}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
    <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '4px', fontWeight: '500' }}>
      {label}
    </div>
  </div>
);

export default FeedbackAdminScreen;
